import React, { FC, useCallback, useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import Alert from '../../../components/bootstrap/Alert';
import { ForgotPasswordapi } from '../../../services/auth';

interface ILoginProps {
    isSignUp?: boolean;
}
const ForgotPassword: FC<ILoginProps> = ({ isSignUp }) => {
    const { setUser } = useContext(AuthContext);
    const { darkModeStatus } = useDarkMode();
    const navigate = useNavigate();

    const [signInPassword, setSignInPassword] = useState<boolean>(false);
    const [singUpStatus, setSingUpStatus] = useState<boolean>(!!isSignUp);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [apiError, setApiError] = useState<string | null>(null);

    // Validation schema for login
    const loginValidationSchema = Yup.object().shape({
        email: Yup.string()
            .email('Invalid email address')
            .required('Email is required'),
    });


    const formik = useFormik({
        enableReinitialize: true,
        initialValues: { email: '' },
        validationSchema: loginValidationSchema,
        validateOnChange: false,
        onSubmit: async (values) => {
            setIsLoading(true);
            setApiError(null);

            try {

                let response;
                // Signup flow
                response = await ForgotPasswordapi({
                    email: values.email
                });

                // Check if there's an error in the response
                if (response?.error) {
                    setApiError(response?.error);
                    setIsLoading(false);
                    return;
                }
                navigate('/auth-pages/reset-password');
            } catch (error) {
                setApiError('An unexpected error occurred');
            } finally {
                setIsLoading(false);
            }
        },
    });

    // Reset error when switching between login and signup
    const handleToggleMode = () => {
        setSignInPassword(false);
        setSingUpStatus(!singUpStatus);
        setApiError(null);
        formik.resetForm();
    };

    return (
        <PageWrapper
            isProtected={false}
            title={singUpStatus ? 'Sign Up' : 'Login'}
            className={classNames({ 'bg-dark': !singUpStatus, 'bg-light': singUpStatus })}>
            <Page className='p-0'>
                <div className='row h-100 align-items-center justify-content-center'>
                    <div className='col-xl-4 col-lg-6 col-md-8 shadow-3d-container'>
                        <Card className='shadow-3d-dark' data-tour='login-page'>
                            <CardBody>
                                <div className='text-center my-5'>
                                    <Link
                                        to='/'
                                        className={classNames(
                                            'text-decoration-none  fw-bold display-2',
                                            {
                                                'text-dark': !darkModeStatus,
                                                'text-light': darkModeStatus,
                                            },
                                        )}
                                        aria-label='Facit'>
                                        <Logo width={200} />
                                    </Link>
                                </div>
                                {apiError && (
                                    <Alert color='danger' isLight icon='Error' isDismissible>
                                        {apiError}
                                    </Alert>
                                )}

                                <form className='row g-4' onSubmit={formik.handleSubmit}>
                                    <div className='col-12'>
                                        <FormGroup
                                            id='email'
                                            isFloating
                                            label='Email'>
                                            <Input
                                                type='email'
                                                name='email'
                                                value={formik.values.email}
                                                onChange={formik.handleChange}
                                                isTouched={formik.touched.email}
                                                invalidFeedback={formik.errors.email}
                                            />
                                        </FormGroup>
                                    </div>
                                    <div className='col-12 d-flex gap-3'>
                                        <Button
                                            color='warning'
                                            className='w-50 py-3'
                                            type='button' // Change type to 'button'
                                            onClick={() => navigate('/auth-pages/login')} // Navigate to login page
                                            isDisable={isLoading}
                                            isOutline // Add outline prop or class for outlined style
                                        >
                                            {isLoading ? <Spinner isSmall inButton isGrow /> : 'Back'}
                                        </Button>
                                        <Button
                                            color='warning'
                                            className='w-50 py-3'
                                            type='submit'
                                            isDisable={isLoading}>
                                            {isLoading ? <Spinner isSmall inButton isGrow /> : 'Reset Password'}
                                        </Button>
                                    </div>
                                    {/* <div className="text-center mt-3">
                                        <p>
                                            Already have an account?{' '}
                                            <Button
                                                color="link"
                                                className="p-0"
                                                onClick={handleToggleMode}
                                                style={{ textDecoration: 'underline' }}
                                            >
                                                Back
                                            </Button>
                                        </p>
                                    </div> */}
                                    {/* Mode Toggle Buttons */}
                                    {/* <div className='col-12 mt-3'>
                                        <div className='row row-cols-2 g-3'>
                                            <div className='col'>
                                                <Button
                                                    color={darkModeStatus ? 'light' : 'dark'}
                                                    isLight={singUpStatus}
                                                    className='rounded-1 w-100'
                                                    size='lg'
                                                    onClick={handleToggleMode}>
                                                    Login
                                                </Button>
                                            </div>
                                            <div className='col'>
                                                <Button
                                                    color={darkModeStatus ? 'light' : 'dark'}
                                                    isLight={!singUpStatus}
                                                    className='rounded-1 w-100'
                                                    size='lg'
                                                    onClick={handleToggleMode}>
                                                    Sign Up
                                                </Button>
                                            </div>
                                        </div>
                                    </div> */}
                                </form>
                            </CardBody>
                        </Card>
                        <div className='text-center'>
                            <a
                                href='/'
                                className={classNames('text-decoration-none me-3', {
                                    'link-light': singUpStatus,
                                    'link-dark': !singUpStatus,
                                })}>
                                Privacy policy
                            </a>
                            <a
                                href='/'
                                className={classNames('link-light text-decoration-none', {
                                    'link-light': singUpStatus,
                                    'link-dark': !singUpStatus,
                                })}>
                                Terms of use
                            </a>
                        </div>
                    </div>
                </div>
            </Page>
        </PageWrapper>
    );
};

export default ForgotPassword;
